import React, { useState, useEffect, useMemo } from 'react';
import { organizationsApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';

import { tokens } from '@design';
import { getTextAlign } from '../../utils/rtl';
import { useLanguage } from '../../contexts/LanguageContext';
interface Props {
  organizationId: number;
  onSave: () => void;
  onClose: () => void;
}

const PRESET_COLORS = [
  '#2563EB', tokens.colors.raw.blue["500"], '#06B6D4', '#14B8A6',
  tokens.colors.raw.green["500"], '#0F9CD8', tokens.colors.raw.yellow["500"], tokens.colors.raw.orange["500"],
  tokens.colors.raw.red["500"], '#EC4899', '#8B5CF6', '#6366F1',
  '#171717', tokens.colors.raw.gray["700"], tokens.colors.raw.gray["500"], tokens.colors.semantic.general["Main Color"],
];

export default function OrgChartColorSettings({ organizationId, onSave, onClose }: Props) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { showSuccess, showError } = useNotification();
  const [primaryColor, setPrimaryColor] = useState('#2563EB');
  const [secondaryColor, setSecondaryColor] = useState('#0F9CD8');
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState<'primary' | 'secondary'>('primary');

  useEffect(() => {
    (async () => {
      try {
        const org = await organizationsApi.get(organizationId);
        setPrimaryColor(org.primary_color || '#2563EB');
        setSecondaryColor(org.secondary_color || '#0F9CD8');
      } catch {
        // use defaults
      }
    })();
  }, [organizationId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await organizationsApi.update(organizationId, {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });
      showSuccess('تم حفظ ألوان الهيكل التنظيمي');
      onSave();
    } catch {
      showError('فشل في حفظ الألوان');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetClick = (color: string) => {
    if (activeField === 'primary') {
      setPrimaryColor(color);
    } else {
      setSecondaryColor(color);
    }
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>ألوان الهيكل التنظيمي</span>
        <span style={styles.closeBtn} onClick={onClose}>✕</span>
      </div>

      {/* Primary Color */}
      <div
        style={{
          ...styles.colorRow,
          border: activeField === 'primary' ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: tokens.radii.absolute["radius-8"],
          padding: tokens.spacing.absolute["2_5"],
          cursor: 'pointer',
        }}
        onClick={() => setActiveField('primary')}
      >
        <label style={styles.label}>اللون الأساسي (الأقسام والإدارات)</label>
        <div style={styles.inputRow}>
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            style={styles.colorInput}
          />
          <input
            type="text"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            onFocus={() => setActiveField('primary')}
            style={styles.textInput}
            maxLength={7}
          />
          <div style={{ ...styles.previewNode, backgroundColor: primaryColor, color: '#fff' }}>
            معاينة
          </div>
        </div>
      </div>

      {/* Secondary Color */}
      <div
        style={{
          ...styles.colorRow,
          border: activeField === 'secondary' ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: tokens.radii.absolute["radius-8"],
          padding: tokens.spacing.absolute["2_5"],
          cursor: 'pointer',
        }}
        onClick={() => setActiveField('secondary')}
      >
        <label style={styles.label}>اللون الثانوي (اسم الجهة)</label>
        <div style={styles.inputRow}>
          <input
            type="color"
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            style={styles.colorInput}
          />
          <input
            type="text"
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            onFocus={() => setActiveField('secondary')}
            style={styles.textInput}
            maxLength={7}
          />
          <div style={{ ...styles.previewNode, backgroundColor: secondaryColor, color: '#fff' }}>
            معاينة
          </div>
        </div>
      </div>

      {/* Preset swatches */}
      <div style={styles.swatchSection}>
        <label style={styles.label}>
          ألوان سريعة — {activeField === 'primary' ? 'اللون الأساسي' : 'اللون الثانوي'}
        </label>
        <div style={styles.swatchGrid}>
          {PRESET_COLORS.map((color) => {
            const isActive = activeField === 'primary'
              ? primaryColor === color
              : secondaryColor === color;
            return (
              <div
                key={color}
                onClick={() => handlePresetClick(color)}
                style={{
                  ...styles.swatch,
                  backgroundColor: color,
                  outline: isActive ? '2px solid #111' : 'none',
                  outlineOffset: 1,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        <button onClick={onClose} style={styles.cancelBtn}>إلغاء</button>
        <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
          {saving ? 'جاري الحفظ...' : 'حفظ الألوان'}
        </button>
      </div>
    </div>
  );
}

const getStyles = (isRTL: boolean): Record<string, React.CSSProperties> => ({
  panel: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    border: '1px solid #E5E7EB',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    padding: tokens.spacing.absolute["4"],
    width: 320,
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["3"],
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
    color: tokens.colors.raw.gray["900"],
  },
  closeBtn: {
    cursor: 'pointer',
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.gray["500"],
    padding: '2px 6px',
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  colorRow: {
    marginBottom: tokens.spacing.absolute["2_5"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.gray["500"],
    marginBottom: tokens.spacing.absolute["1_5"],
    display: 'block',
    textAlign: getTextAlign(isRTL),
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  colorInput: {
    width: 36,
    height: 36,
    border: 'none',
    borderRadius: tokens.radii.absolute["radius-6"],
    cursor: 'pointer',
    padding: tokens.spacing.absolute["0"],
    background: 'none',
  },
  textInput: {
    flex: 1,
    height: 34,
    border: '1px solid #E5E7EB',
    borderRadius: tokens.radii.absolute["radius-6"],
    paddingInlineStart: tokens.spacing.absolute["2_5"],
    paddingInlineEnd: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: 'monospace',
    color: tokens.colors.raw.gray["900"],
    backgroundColor: tokens.colors.raw.gray["50"],
    outline: 'none',
  },
  previewNode: {
    padding: '6px 12px',
    borderRadius: tokens.radii.absolute["radius-6"],
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    textAlign: 'center' as const,
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
    whiteSpace: 'nowrap' as const,
  },
  swatchSection: {
    marginBottom: tokens.spacing.absolute["3"],
  },
  swatchGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: tokens.spacing.absolute["1_5"],
  },
  swatch: {
    width: 28,
    height: 28,
    borderRadius: tokens.radii.absolute["radius-6"],
    cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.1)',
    transition: 'transform 0.1s',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacing.absolute["2"],
    justifyContent: 'flex-start',
    borderTop: '1px solid #E5E7EB',
    paddingTop: tokens.spacing.absolute["3"],
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: tokens.radii.absolute["radius-6"],
    border: '1px solid #E5E7EB',
    backgroundColor: tokens.colors.raw.white,
    cursor: 'pointer',
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
    color: tokens.colors.raw.gray["500"],
  },
  saveBtn: {
    padding: '8px 20px',
    borderRadius: tokens.radii.absolute["radius-6"],
    border: 'none',
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    color: tokens.colors.raw.white,
    cursor: 'pointer',
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
  },
});

const styles = getStyles(true);
